'use client';

import { GET } from "@/api/get-request";
import { PaymentService } from "@/types/payment";
import React, { createContext, useContext } from "react";
import { useFormik } from "formik";
import * as yup from 'yup';
import { POST } from "@/api/post-requests";
import { useRouter, useSearchParams } from "next/navigation";
import { useNotifications } from "@toolpad/core/useNotifications";
import { Application } from "@/types/application";
import { useAuth } from "./with-auth";
import Cookies from 'js-cookie';
import parsePhoneNumber from 'libphonenumber-js';
import { useTranslations } from "next-intl";

type FormValues = {
    service_id: string,
    amount: string,
    full_name: string,
    email: string,
    number: string,
    payment_method: string,
    prefix: string,
    program: string,
};

export type CustomizationProps = {
    services: PaymentService[],
    GetServices: () => void,
    formik: ReturnType<typeof useFormik<FormValues>>,
    loading: boolean,
    application: Application | null,
    application_id: string | null,
    package_id: string | null,
    applications: Application[]
    payment_gateways: { value: string, label: string }[],
    phone_codes: { value: string, label: string }[],
    DownloadInvoice: (id: number) => void;
    DownloadAllInvoices: () => void;
    activeTab: string;
    setActiveTab: (tab: string) => void;
    // Stripe Embedded Checkout fields
    clientSecret: string | null;
    publishableKey: string | null;
    paymentId: number | null;
    checkoutAmount: number | null;
    checkoutService: string | null;
    setClientSecret: (secret: string | null) => void;
    setPublishableKey: (key: string | null) => void;
    setPaymentId: (id: number | null) => void;
    setCheckoutAmount: (amount: number | null) => void;
    setCheckoutService: (service: string | null) => void;
};

const PLAN_SERVICE_SLUG: Record<string, string> = {
    basic: 'normal',
    advisor: 'advisor'
};

const PaymentContext = createContext<CustomizationProps | null>(null);

export const PaymentProvider = ({ children }: { children: React.ReactNode }) => {

    const [services, setServices] = React.useState<PaymentService[]>([]);
    const [application, setApplication] = React.useState<Application | null>(null);
    const [applications, setApplications] = React.useState<Application[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const notifications = useNotifications();
    const searchParams = useSearchParams();
    const application_id = searchParams ? searchParams.get('application') : null;
    const package_id = searchParams ? searchParams.get('package') : null;
    const plan = searchParams ? searchParams.get('plan')?.toLowerCase() : null;
    const [payment_gateways, setPaymentGateways] = React.useState<{ value: string, label: string }[]>([]);
    const [phone_codes, setPhoneCodes] = React.useState<{ value: string, label: string }[]>([]);
    const [activeTab, setActiveTab] = React.useState<string>('funding');
    const { user } = useAuth();
    const tValid = useTranslations('payments.validations');

    // Stripe Embedded Checkout state
    const [clientSecret, setClientSecret] = React.useState<string | null>(null);
    const [publishableKey, setPublishableKey] = React.useState<string | null>(null);
    const [paymentId, setPaymentId] = React.useState<number | null>(null);
    const [checkoutAmount, setCheckoutAmount] = React.useState<number | null>(null);
    const [checkoutService, setCheckoutService] = React.useState<string | null>(null);

    React.useLayoutEffect(() => {

        const GetSelectData = async (url: string) => {
            const response = await GET({ url: `v1/enums/${url}` });
            if (response.success)
                return response.data
            else return []
        };

        const GetData = async () => {

            setPaymentGateways(await GetSelectData('payment_gateways'));
            setPhoneCodes(await GetSelectData('phone_codes'));

        };

        GetData();
        GetServices();
    }, []);


    const DownloadPDF = (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', 'invoice.pdf');
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
    };


    const DownloadInvoice = async (id: number) => {
        const token = await Cookies.get('token') || '';
        const response = await fetch(`${process.env.URI}v1/payments/${id}/invoice`, {
            method: 'GET',
            headers: {
                'accept': 'application/pdf',
                'Content-Type': 'application/json',
                'Authorization': token !== '' ? `Bearer ${token}` : ''
            }
        });

        if (response)
            DownloadPDF(await response.blob());
    };

    const DownloadAllInvoices = async () => {
        const token = await Cookies.get('token') || '';
        const response = await fetch(`${process.env.URI}v1/payments/invoices`, {
            method: 'GET',
            headers: {
                'accept': 'application/pdf',
                'Content-Type': 'application/json',
                'Authorization': token !== '' ? `Bearer ${token}` : ''
            }
        });
        if (response)
            DownloadPDF(await response.blob());
    };

    const updateData = () => {
        if (application_id !== null) {
            formik.setFieldValue('service_id', services.find(item => item.title == 'Application Fee')?.id.toString() || "0");
            GetApplicationByID();
        } else if (package_id !== null) {
            formik.setFieldValue('service_id', services.find(item => item.id.toString() == package_id)?.id.toString() || "0");
            formik.setFieldValue('amount', services.find(item => item.id.toString() === package_id)?.price || "0");
        } else {
            const planSlug = plan ? PLAN_SERVICE_SLUG[plan] : undefined;
            if (planSlug) {
                const matchedService = services.find(item => item.slug === planSlug);
                if (matchedService) {
                    formik.setFieldValue('service_id', matchedService.id.toString());
                    // اگر قیمت سفارشی از URL آمده باشد، از آن استفاده کن
                    formik.setFieldValue('amount', matchedService.price?.toString() || "0");
                }
            }
            GetApplications();
        }
    };


    const GetServices = async () => {
        const response = await GET({ url: 'v1/service-payments' });
        if (response.success) {
            setServices(response.data);
        } else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
    };
    const router = useRouter();

    const validationSchema = yup.object({
        full_name: yup
            .string()
            .required(tValid('fullName_req')),
        payment_method: yup
            .string()
            .required(tValid('Payment_req')),
        email: yup
            .string()
            .required(tValid('Email_req')),
        number: yup
            .string()
            .required(tValid('PhoneNumber_req')),
        service_id: yup
            .string()
            .required(tValid('Service_req'))
            .test('add-ons-email-package', tValid('package_req'), function (value) {
                // اگر service_id برابر با "-1" باشد (Add-Ons)، باید یک Email Package انتخاب شده باشد
                // اما وقتی Email Package انتخاب می‌شود، service_id به ID آن Email Package تغییر می‌کند
                // پس اگر service_id === "-1" باشد، یعنی Add-Ons انتخاب شده اما Email Package انتخاب نشده
                if (value === "-1") {
                    return false; // خطا: باید Email Package انتخاب شود
                }
                return true;
            }),
    });


    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues: {
            service_id: "",
            amount: "0",
            full_name: (user?.first_name && user?.last_name)
                ? `${user.first_name} ${user.last_name}`.trim()
                : "",
            email: user?.email || "",
            number: parsePhoneNumber(user?.mobile_number || '')?.nationalNumber || "",
            payment_method: "",
            prefix: parsePhoneNumber(user?.mobile_number || '')?.country || "CA",
            program: ""
        },
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: async (values) => {
            // Debug: Log full_name values
            console.log('user?.first_name:', user?.first_name);
            console.log('user?.last_name:', user?.last_name);
            console.log('values.full_name:', values.full_name);
            console.log('values.full_name length:', values.full_name?.length);
            console.log('values.full_name trimmed:', values.full_name?.trim().length);

            // Set all fields as touched to show validation errors
            formik.setTouched({
                full_name: true,
                email: true,
                number: true,
                payment_method: true,
                service_id: true,
                program: true
            });

            // Wait for state update before validating
            await new Promise(resolve => setTimeout(resolve, 0));

            // Validate before submitting
            const errors = await formik.validateForm();
            console.log('Validation errors:', errors);
            if (Object.keys(errors).length > 0) {
                setLoading(false);
                // Show notification for service_id error if exists
                if (errors.service_id) {
                    notifications.show(errors.service_id, {
                        severity: 'error',
                        autoHideDuration: 3000,
                    });
                }
                return;
            }

            setLoading(true);

            const payload = {
                amount: values.amount,
                service_id: values.service_id,
                full_name: values.full_name,
                email: values.email,
                number: '+' + phone_codes.find(item => item.label === values.prefix)?.value + values.number,
                payment_method: values.payment_method
            };

            if (application_id !== null) {
                payload['application_id'] = application_id;
            }

            const response = await POST({
                url: 'v1/payments',
                body: payload
            });

            if (response.success) {
                const data = response.data;

                // Check response mode
                if (data?.mode === 'embedded_form') {
                    // Embedded checkout flow
                    if (data.clientSecret && data.publishableKey && data.payment_id) {
                        // Store Stripe credentials in context
                        setClientSecret(data.clientSecret);
                        setPublishableKey(data.publishableKey);
                        setPaymentId(data.payment_id);

                        // Store paymentId in sessionStorage for page refresh recovery
                        if (typeof window !== 'undefined') {
                            sessionStorage.setItem('stripe_payment_id', String(data.payment_id));
                        }

                        // Navigate to payment page
                        router.push('/payments/new-payment/payment');
                    } else {
                        console.error('Missing embedded checkout data:', data);
                        notifications.show('Embedded checkout data incomplete', {
                            severity: 'error',
                            autoHideDuration: 5000,
                        });
                    }
                } else if (data?.checkout_url) {
                    // Hosted checkout flow (redirect to Stripe)
                    router.push(data.checkout_url);
                } else {
                    console.error('Invalid response data:', data);
                    notifications.show('Invalid payment response from server', {
                        severity: 'error',
                        autoHideDuration: 5000,
                    });
                }
            } else {
                Object.keys(response.errors).forEach((key) => formik.setFieldError(`${key}`, response.errors[key]));
                notifications.show(response.message, {
                    severity: 'error',
                    autoHideDuration: 3000,
                });
            }
            setLoading(false);

        },
    });

    // Clear service_id error when Email Package is selected (service_id is no longer "-1")
    React.useEffect(() => {
        if (formik.values.service_id !== "-1" && formik.errors.service_id) {
            formik.setFieldError('service_id', undefined);
        }
    }, [formik.values.service_id, formik.errors.service_id]);

    const GetApplicationByID = async () => {
        const response = await GET({
            url: `v1/applications/${application_id}`
        });
        if (response.success) {
            setApplication(response.data);
            formik.setFieldValue('program', response.data.program.name);
            formik.setFieldValue('amount', response.data.program.application_fee);
        } else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
    };

    React.useEffect(() => {
        if (application !== null) {
            formik.setFieldValue('program', application?.program.name);
            formik.setFieldValue('amount', application?.program.application_fee?.toString());
        }

    }, [application]);

    React.useEffect(() => {
        if (services.length > 0)
            updateData();
    }, [services]);

    const GetApplications = async () => {
        const response = await GET({
            url: `v1/applications`
        });
        if (response.success)
            setApplications(response.data);
        else
            notifications.show(response.message, {
                severity: 'error',
                autoHideDuration: 3000,
            });
    };

    React.useEffect(() => {
        updateData();
    }, []);

    React.useEffect(() => {
        // فقط وقتی program انتخاب شده، amount را از application_fee بگیر
        // این از override شدن amount برای Basic packages و سایر سرویس‌ها جلوگیری می‌کند
        if (formik.values.program && formik.values.program.trim() !== '') {
            const matchedApplication = applications.find(item => item.program.name === formik.values.program);
            if (matchedApplication) {
                formik.setFieldValue('amount', matchedApplication.program.application_fee?.toString() || '0');
            }
        }
    }, [formik.values.program]);

    return <PaymentContext.Provider
        value={{
            services,
            GetServices,
            formik,
            loading,
            application,
            application_id,
            package_id,
            applications,
            payment_gateways,
            phone_codes,
            DownloadInvoice,
            DownloadAllInvoices,
            activeTab,
            setActiveTab,
            // Stripe Embedded Checkout
            clientSecret,
            publishableKey,
            paymentId,
            checkoutAmount,
            checkoutService,
            setClientSecret,
            setPublishableKey,
            setPaymentId,
            setCheckoutAmount,
            setCheckoutService
        }}
    >
        {children}
    </PaymentContext.Provider>
};


export const usePayment = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error("usePayment must be used within an PaymentProvider");
    }
    return context;
};
